import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCatalogo } from "../../../services/backend/catalogService";
import { buildCatalogSearchPath } from "../../utils/topbarUtils";
import logger from "../../utils/logger";

const useTopbarSearch = ({ selectedCollection, selectedSubcollection, menuStructure, selectedAlmacen, toastRef }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const isVoiceSupported = useMemo(
    () => typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
    []
  );

  const handleSearch = useCallback(async (term) => {
    const trimmedSearch = term.trim();
    const { selectedFamilyId, url } = buildCatalogSearchPath({
      selectedCollection,
      selectedSubcollection,
      trimmedSearch,
      menuStructure,
    });
    try {
      await getCatalogo({
        almacen: selectedAlmacen,
        descripcion: trimmedSearch,
        familia: selectedFamilyId,
        fotos: false,
        start: 0,
        length: 20,
        precios: false,
        stocks: false,
      });
    } catch (error) {
      logger.error("No fue posible ejecutar la búsqueda del catálogo.", error);
      toastRef.current?.show({ severity: "error", summary: "Búsqueda", detail: "No fue posible ejecutar la búsqueda del catálogo.", life: 4000 });
    }
    navigate(url);
  }, [selectedCollection, selectedSubcollection, menuStructure, selectedAlmacen, navigate, toastRef]);

  const handleSearchSubmit = useCallback(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleSearchSubmit();
  }, [handleSearchSubmit]);

  const handleVoiceInput = useCallback((toastRef) => {
    if (!isVoiceSupported) {
      toastRef?.current?.show({ severity: "warn", summary: "Dictado no disponible", detail: "Tu navegador no soporta reconocimiento de voz.", life: 3500 });
      return;
    }
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = recognitionRef.current || new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = "es-CL";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map((r) => r[0]?.transcript || "").join(" ").trim();
      if (transcript) setSearchTerm(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    try { recognition.start(); } catch { setIsListening(false); }
  }, [isVoiceSupported, isListening]);

  return {
    searchTerm,
    setSearchTerm,
    isListening,
    isVoiceSupported,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleVoiceInput,
  };
};

export default useTopbarSearch;
